"use client";

import {
  DISPLAY,
  EDITORIAL_ACTION_CLS,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  INK,
  SURFACE_DARK,
  mix,
} from "@/components/editorial";
import { EvidenceSourcesGrid } from "@/components/editorial/evidence-sources-grid";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CREDIT_PACKS,
  FDE_DAY_RATE,
  MAX_FDE_DAYS,
  TWIN_PLANS,
} from "./pricing-data";
import { PricingPlanCard } from "./pricing-plan-card";
import { PricingSliderShell } from "./pricing-slider-shell";

const gbp = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 0,
});

const AI_PACK_FEATURES = [
  {
    title: "Your choice of models",
    detail:
      "Use approved models for each workflow without managing separate provider accounts.",
  },
  {
    title: "Capacity that grows",
    detail:
      "Move between packs as more people and approved workflows use the Twin.",
  },
  {
    title: "See your usage",
    detail:
      "See AI usage and provider costs where the model provider makes them available.",
  },
] as const;

const FDE_BENEFITS = [
  {
    title: "Work with our engineers",
    detail:
      "Work directly with engineers on your systems, business questions and the results you need.",
  },
  {
    title: "Faster path to live use",
    detail:
      "Connect the systems for a useful workflow and check the results with your team.",
  },
  {
    title: "Expand where it helps",
    detail:
      "Agree additional connections and workflow improvements as your needs change.",
  },
] as const;

export function PricingBuilder() {
  const [planId, setPlanId] = useState("core");
  const [creditIndex, setCreditIndex] = useState(1);
  const [fdeDays, setFdeDays] = useState(0);
  const [showPackageBar, setShowPackageBar] = useState(false);
  const builderSectionRef = useRef<HTMLElement>(null);

  const plan = TWIN_PLANS.find((item) => item.id === planId) ?? TWIN_PLANS[0];
  const creditPack = CREDIT_PACKS[creditIndex] ?? CREDIT_PACKS[0];
  const fdeMonthly = fdeDays * FDE_DAY_RATE;
  const estimatedTotal = `From ${gbp.format(
    plan.monthlyPrice + creditPack.monthlyPrice + fdeMonthly,
  )} / month`;
  const packageItems = [
    { label: `${plan.name} Twin`, value: plan.price },
    { label: creditPack.name, value: creditPack.price },
    {
      label: "Engineering support",
      value: fdeDays === 0 ? "£0" : gbp.format(fdeMonthly),
    },
  ];

  useEffect(() => {
    const updatePackageBar = () => {
      const section = builderSectionRef.current;
      if (!section) return;
      const bounds = section.getBoundingClientRect();
      setShowPackageBar(bounds.top < window.innerHeight && bounds.bottom > 0);
    };

    updatePackageBar();
    window.addEventListener("scroll", updatePackageBar, { passive: true });
    window.addEventListener("resize", updatePackageBar);
    return () => {
      window.removeEventListener("scroll", updatePackageBar);
      window.removeEventListener("resize", updatePackageBar);
    };
  }, []);

  const enquiryHref = useMemo(() => {
    const subject = "Your Twin package enquiry";
    const body = [
      "Hello Mercury Labs,",
      "",
      "I would like to discuss this package for our Twin:",
      `- Twin plan: ${plan.name} (${plan.price}${plan.cadence ?? ""})`,
      `- Model usage: ${creditPack.name} (${creditPack.price})`,
      `- Engineering support: ${fdeDays} day${fdeDays === 1 ? "" : "s"} per month (${gbp.format(fdeMonthly)})`,
      `- Current estimate: ${estimatedTotal}`,
      "",
      "The workflow we want to start with is:",
      "",
    ].join("\n");
    return `mailto:team@mercurylabs.io?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }, [creditPack, estimatedTotal, fdeDays, fdeMonthly, plan]);

  return (
    <>
      <section
        ref={builderSectionRef}
        className="relative pb-28 md:pb-36"
        style={{ backgroundColor: GROUND, color: INK }}
      >
        <div className={EDITORIAL_CONTAINER_CLS}>
          <div
            className="border-t pt-12"
            style={{ borderColor: mix("--theme-ink", 18) }}
          >
            <div>
              <fieldset>
                <legend className="sr-only">Choose your Twin plan</legend>
                <div className="grid gap-3 md:grid-cols-3">
                  {TWIN_PLANS.map((item) => (
                    <PricingPlanCard
                      key={item.id}
                      plan={item}
                      selected={item.id === planId}
                      onSelect={() => setPlanId(item.id)}
                    />
                  ))}
                </div>
              </fieldset>

              <div className="mt-14 space-y-4">
                <PricingSliderShell
                  eyebrow="Model usage"
                  title="Choose an AI credit pack"
                  description={creditPack.description}
                  value={`${creditPack.name} · ${creditPack.price}`}
                  featuresLabel="What an AI pack adds"
                  features={AI_PACK_FEATURES}
                >
                  <input
                    aria-label="Model credit pack"
                    type="range"
                    min={0}
                    max={CREDIT_PACKS.length - 1}
                    aria-valuetext={`${creditPack.name}, ${creditPack.price}`}
                    step={1}
                    value={creditIndex}
                    onChange={(event) =>
                      setCreditIndex(Number(event.target.value))
                    }
                    className="h-2 w-full cursor-pointer"
                    style={{ accentColor: SURFACE_DARK }}
                  />
                  <div className="mt-3 grid grid-cols-4 text-[0.72rem] font-medium">
                    {CREDIT_PACKS.map((pack, index) => (
                      <span
                        key={pack.id}
                        className={
                          index === 0
                            ? "text-left"
                            : index === CREDIT_PACKS.length - 1
                              ? "text-right"
                              : "text-center"
                        }
                        style={{
                          color: mix(
                            "--theme-ink",
                            index === creditIndex ? 90 : 66,
                          ),
                        }}
                      >
                        {pack.shortName}
                      </span>
                    ))}
                  </div>
                </PricingSliderShell>

                <PricingSliderShell
                  eyebrow="Optional support"
                  title="Add engineering support"
                  description="Reserve engineering time to connect your systems, check a first workflow or extend your Twin. We agree the work with you."
                  value={
                    fdeDays === 0
                      ? "No engineering days"
                      : `${fdeDays} day${fdeDays === 1 ? "" : "s"} · ${gbp.format(fdeMonthly)} / month`
                  }
                  featuresLabel="How our engineers help"
                  features={FDE_BENEFITS}
                >
                  <input
                    aria-label="Engineering days per month"
                    type="range"
                    min={0}
                    max={MAX_FDE_DAYS}
                    aria-valuetext={`${fdeDays} day${fdeDays === 1 ? "" : "s"} a month, ${gbp.format(fdeMonthly)}`}
                    step={1}
                    value={fdeDays}
                    onChange={(event) =>
                      setFdeDays(Number(event.target.value))
                    }
                    className="h-2 w-full cursor-pointer"
                    style={{ accentColor: SURFACE_DARK }}
                  />
                  <div
                    className="mt-3 flex justify-between text-[0.72rem] font-medium"
                    style={{ color: mix("--theme-ink", 66) }}
                  >
                    <span>0</span>
                    <span>5</span>
                    <span>10</span>
                    <span>15</span>
                    <span>20 days</span>
                  </div>
                  <p
                    className="mt-5 text-[0.78rem]"
                    style={{ color: mix("--theme-ink", 66) }}
                  >
                    Calculated at {gbp.format(FDE_DAY_RATE)} per day. Larger or
                    dedicated teams are agreed separately.
                  </p>
                </PricingSliderShell>

                <EvidenceSourcesGrid />
              </div>
            </div>
          </div>
        </div>

        {showPackageBar ? (
          <div
            className="fixed inset-x-0 bottom-0 z-40 border-t"
            style={{
              backgroundColor: SURFACE_DARK,
              borderColor: mix("--theme-ground", 18),
              boxShadow: "0 -8px 24px rgba(0,0,0,0.18)",
              color: GROUND,
            }}
          >
            <div className={`${EDITORIAL_CONTAINER_CLS} py-3 md:py-4`}>
              <div className="flex items-center justify-between gap-4 lg:hidden">
                <div className="min-w-0">
                  <p
                    className="text-[0.68rem] font-medium tracking-[0.12em] uppercase"
                    style={{ color: mix("--theme-ground", 60) }}
                  >
                    Your Twin package
                  </p>
                  <p
                    className="mt-1 truncate text-[1.05rem] font-semibold"
                    style={DISPLAY}
                  >
                    {estimatedTotal}
                  </p>
                </div>
                <a
                  href={enquiryHref}
                  className={`${EDITORIAL_ACTION_CLS} shrink-0`}
                  style={{ backgroundColor: "var(--theme-accent)", color: INK }}
                >
                  Discuss package
                </a>
              </div>

              <div className="hidden items-center gap-7 lg:grid lg:grid-cols-[12rem_repeat(3,minmax(0,1fr))_11rem]">
                <div>
                  <p
                    className="text-[0.68rem] font-medium tracking-[0.12em] uppercase"
                    style={{ color: mix("--theme-ground", 60) }}
                  >
                    Your Twin package
                  </p>
                  <p
                    className="mt-1.5 text-[1.15rem] font-semibold"
                    style={DISPLAY}
                  >
                    {estimatedTotal}
                  </p>
                </div>
                {packageItems.map((item) => (
                  <div
                    key={item.label}
                    className="min-w-0 border-l pl-7"
                    style={{ borderColor: mix("--theme-ground", 18) }}
                  >
                    <p
                      className="truncate text-[0.68rem] font-medium tracking-[0.12em] uppercase"
                      style={{ color: mix("--theme-ground", 58) }}
                    >
                      {item.label}
                    </p>
                    <p className="mt-1 text-[0.9rem] font-semibold">
                      {item.value}
                    </p>
                  </div>
                ))}
                <a
                  href={enquiryHref}
                  className={`${EDITORIAL_ACTION_CLS} w-full`}
                  style={{ backgroundColor: "var(--theme-accent)", color: INK }}
                >
                  Discuss package
                </a>
              </div>
            </div>
          </div>
        ) : null}
      </section>

      <section
        className="relative py-20 md:py-24"
        style={{ backgroundColor: GROUND, color: INK }}
      >
        <div className={EDITORIAL_CONTAINER_CLS}>
          <div className="grid gap-12 lg:grid-cols-3 lg:gap-16">
            <div>
              <h2
                className="text-[2rem] font-semibold tracking-[-0.025em]"
                style={DISPLAY}
              >
                Clear boundaries before you buy.
              </h2>
            </div>
            <div
              className="border-t pt-5"
              style={{ borderColor: mix("--theme-ink", 18) }}
            >
              <h3 className="font-semibold" style={DISPLAY}>
                Three operating scopes
              </h3>
              <p
                className="mt-3 text-[0.94rem] leading-[1.6]"
                style={{ color: mix("--theme-ink", 76) }}
              >
                Core starts at £2,000 per month, Scale at £5,000 and Enterprise
                at £15,000. Final scope reflects the teams, systems and
                governance the deployment must support.
              </p>
            </div>
            <div
              className="border-t pt-5"
              style={{ borderColor: mix("--theme-ink", 18) }}
            >
              <h3 className="font-semibold" style={DISPLAY}>
                Add-ons stay adjustable
              </h3>
              <p
                className="mt-3 text-[0.94rem] leading-[1.6]"
                style={{ color: mix("--theme-ink", 76) }}
              >
                Credit packs and engineering capacity can change with adoption.
                Model rates, new source systems, unusual deployment requirements
                and external services are confirmed before work starts.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
